import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import ApiInspector from '../../../components/ApiInspector';
import BopExcelTable, { BopExcelColumn } from '../../../components/BopExcelTable';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { API_DOCS } from './ModelliConfigurazionePage.api';
import './ModelliConfigurazionePage.css';

// ── Icone ─────────────────────────────────────────────────────────────────────

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const GripIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="5" r="1" fill="currentColor" /><circle cx="15" cy="5" r="1" fill="currentColor" />
    <circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" />
    <circle cx="9" cy="19" r="1" fill="currentColor" /><circle cx="15" cy="19" r="1" fill="currentColor" />
  </svg>
);
const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// ── Tipi ──────────────────────────────────────────────────────────────────────

interface ModelloRow {
  id: number;
  codice: string;
  nome: string;
  articoloPadreId: number;
  articoloPadreCodice: string;
  articoloPadreNome: string;
  umVenditaSimbolo: string;
  nComponenti: number;
}

interface ComponenteLocale {
  _key: string;
  id?: number;
  ordine: number;
  tipologia: string;
  categoriaMerceologicaId: number | null;
  categoriaNome: string;
  quantita: number | null;
  isPrincipale: boolean;
  isInDescrizione: boolean;
}

interface ModelloDettaglio {
  id: number;
  societaId: number;
  codice: string;
  nome: string;
  articoloPadreId: number;
  articoloPadreCodice: string;
  articoloPadreNome: string;
  umVenditaSimbolo: string;
  descrizioneTemplate: string;
  componenti: ComponenteLocale[];
}

// Bozza in memoria per la creazione di un nuovo modello (anche da duplicazione).
// Non corrisponde a un record sul DB: viene persistito solo al click su "Salva".
interface DraftModello {
  nome: string;
  articoloPadreId: number;
  articoloPadreCodice: string;
  articoloPadreNome: string;
  umVenditaSimbolo: string;
  descrizioneTemplate: string;
  componenti: ComponenteLocale[];
}

interface ArticoloPadreOption {
  id: number;
  codice: string;
  descrizioneBase: string;
  umVenditaSimbolo: string;
}

interface TipologiaOption {
  valore: string;
  label: string;
}

interface CategoriaOption {
  id: number;
  nome: string;
}

// ── Tooltip ────────────────────────────────────────────────────────────────────

function Tooltip({ text }: { text: string }) {
  const [visible, setVisible] = useState(false);
  return (
    <span
      className="mocon-tooltip"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <InfoIcon />
      {visible && (
        <span className="mocon-tooltip__popup">
          {text}
          <span className="mocon-tooltip__arrow" />
        </span>
      )}
    </span>
  );
}

// ── Autocomplete Articolo Padre ────────────────────────────────────────────────
// Pre-carica all'apertura la lista completa degli articoli PRODOTTO_CONFIGURATO
// della società. Il filtro sul testo digitato avviene in locale (client-side)
// per reattività istantanea. Se la lista è vuota mostra un empty-state.

function AutocompleteArticoloPadre({
  societaId,
  selected,
  onSelect,
  onClear,
}: {
  societaId: string;
  selected: ArticoloPadreOption | null;
  onSelect: (a: ArticoloPadreOption) => void;
  onClear: () => void;
}) {
  const [query, setQuery] = useState('');
  const [allArticoli, setAllArticoli] = useState<ArticoloPadreOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);

  // Pre-caricamento: al mount (o al cambio società) carica l'intera lista.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoaded(false);
      try {
        const params = new URLSearchParams();
        if (societaId) params.set('societaId', societaId);
        // @api GET /api/modelli-configurazione/articoli-padre?societaId -> [{id,codice,descrizioneBase,umVenditaSimbolo}]
        const res = await apiFetch<{ success: boolean; data: ArticoloPadreOption[] }>(
          `/api/modelli-configurazione/articoli-padre?${params}`
        );
        if (!cancelled) {
          setAllArticoli(res.data || []);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setAllArticoli([]);
          setLoaded(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [societaId]);

  // Filtro client-side: match case-insensitive su codice o descrizione.
  const q = query.trim().toLowerCase();
  const filtered = q
    ? allArticoli.filter(a =>
        a.codice.toLowerCase().includes(q) ||
        (a.descrizioneBase || '').toLowerCase().includes(q)
      )
    : allArticoli;

  // Limite visivo per performance del dropdown (fino a 200 righe).
  const visibili = filtered.slice(0, 200);

  const handleSelect = (a: ArticoloPadreOption) => {
    onSelect(a);
    setQuery('');
    setOpen(false);
  };

  if (selected) {
    return (
      <div className="mocon-autocomplete">
        <span className="mocon-selected-badge">
          <strong>{selected.codice}</strong> — {selected.descrizioneBase}
          <button className="mocon-selected-badge__remove" onClick={onClear} title="Rimuovi">✕</button>
        </span>
      </div>
    );
  }

  return (
    <div className="mocon-autocomplete">
      <input
        className="bop-field__input"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        placeholder={
          loading
            ? 'Caricamento articoli configurati...'
            : 'Seleziona o cerca per codice o descrizione...'
        }
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && loaded && (
        <div className="mocon-autocomplete__dropdown">
          {allArticoli.length === 0 ? (
            <div className="mocon-autocomplete__item">
              <div className="bop-text-muted" style={{ fontSize: 'var(--bop-font-sm)' }}>
                Nessun articolo configurato disponibile per questa società
              </div>
            </div>
          ) : visibili.length === 0 ? (
            <div className="mocon-autocomplete__item">
              <div className="bop-text-muted" style={{ fontSize: 'var(--bop-font-sm)' }}>
                Nessun risultato per “{query}”
              </div>
            </div>
          ) : (
            <>
              {visibili.map(a => (
                <div key={a.id} className="mocon-autocomplete__item" onMouseDown={() => handleSelect(a)}>
                  <div className="mocon-autocomplete__item-code">{a.codice}</div>
                  <div className="mocon-autocomplete__item-desc">{a.descrizioneBase}</div>
                  <div className="mocon-autocomplete__item-um">UM: {a.umVenditaSimbolo}</div>
                </div>
              ))}
              {filtered.length > visibili.length && (
                <div className="mocon-autocomplete__item">
                  <div className="bop-text-muted" style={{ fontSize: 'var(--bop-font-xs)' }}>
                    …altri {filtered.length - visibili.length} risultati — affina la ricerca
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Modale Nuovo Modello ──────────────────────────────────────────────────────

function ModalNuovoModello({
  societaId,
  onClose,
  onConfigured,
}: {
  societaId: string;
  onClose: () => void;
  onConfigured: (draft: DraftModello) => void;
}) {
  const [nome, setNome] = useState('');
  const [articoloPadre, setArticoloPadre] = useState<ArticoloPadreOption | null>(null);

  const handleProsegui = () => {
    if (!nome.trim()) { showToast('Il nome è obbligatorio', 'error'); return; }
    if (!articoloPadre) { showToast("L'articolo padre è obbligatorio", 'error'); return; }
    // Nessun POST: si costruisce solo una bozza in memoria. Il record viene
    // creato a DB solo quando l'utente clicca "Salva modello" nella pagina
    // di dettaglio.
    onConfigured({
      nome: nome.trim(),
      articoloPadreId: articoloPadre.id,
      articoloPadreCodice: articoloPadre.codice,
      articoloPadreNome: articoloPadre.descrizioneBase,
      umVenditaSimbolo: articoloPadre.umVenditaSimbolo,
      descrizioneTemplate: '',
      componenti: [],
    });
  };

  return (
    <div className="bop-modal-overlay">
      <div className="bop-modal bop-modal--lg" onClick={e => e.stopPropagation()}>
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">Nuovo modello di configurazione</h2>
          <button className="bop-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="bop-modal__body">
          <div className="bop-form-row">
            <div className="bop-field">
              <label className="bop-field__label">Codice</label>
              <input className="bop-field__input" value="MOCON..." disabled />
              <p className="bop-field__hint">Generato al salvataggio</p>
            </div>
            <div className="bop-field" style={{ flex: 2 }}>
              <label className="bop-field__label">Nome modello <span className="bop-field__required">*</span></label>
              <input
                className="bop-field__input"
                placeholder="Es. Fornitura e posa pavimento gres C2TE"
                value={nome}
                onChange={e => setNome(e.target.value)}
              />
            </div>
          </div>
          <div className="bop-field" style={{ marginTop: 'var(--bop-spacing-4)' }}>
            <label className="bop-field__label">Articolo padre <span className="bop-field__required">*</span></label>
            <AutocompleteArticoloPadre
              societaId={societaId}
              selected={articoloPadre}
              onSelect={setArticoloPadre}
              onClear={() => setArticoloPadre(null)}
            />
            <p className="bop-field__hint">L'unità di misura di vendita sarà ereditata dall'articolo selezionato</p>
          </div>
          <div className="mocon-info-box">
            💡 Dopo aver proseguito potrai aggiungere i componenti e definire il template della descrizione documento. Il modello sarà salvato sul database solo al click su "Salva modello".
          </div>
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          <button className="bop-btn bop-btn-primary" onClick={handleProsegui}>
            Prosegui →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modale Componente ─────────────────────────────────────────────────────────

function ModalComponente({
  componente,
  umVenditaSimbolo,
  societaId,
  onClose,
  onSalva,
}: {
  componente: ComponenteLocale | null;
  umVenditaSimbolo: string;
  societaId: string;
  onClose: () => void;
  onSalva: (c: Omit<ComponenteLocale, '_key' | 'ordine'>) => void;
}) {
  const isEdit = !!componente;
  const [tipologia, setTipologia] = useState(componente?.tipologia || '');
  const [categoriaMerceologicaId, setCategoriaMerceologicaId] = useState<number | null>(componente?.categoriaMerceologicaId ?? null);
  const [categoriaNome, setCategoriaNome] = useState(componente?.categoriaNome || '');
  const [quantita, setQuantita] = useState(
    componente?.quantita !== null && componente?.quantita !== undefined
      ? String(componente.quantita)
      : ''
  );
  const [isPrincipale, setIsPrincipale] = useState(componente?.isPrincipale || false);
  const [isInDescrizione, setIsInDescrizione] = useState(componente?.isInDescrizione || false);
  const [tipologie, setTipologie] = useState<TipologiaOption[]>([]);
  const [categorie, setCategorie] = useState<CategoriaOption[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const params = societaId ? `?societaId=${societaId}` : '';
        // @api GET /api/modelli-configurazione/tipologie?societaId -> [{valore,label}]
        const t = await apiFetch<{ success: boolean; data: TipologiaOption[] }>(
          `/api/modelli-configurazione/tipologie${params}`
        );
        setTipologie(t.data || []);
        // @api GET /api/modelli-configurazione/categorie?societaId&q -> [{id,nome}]
        const c = await apiFetch<{ success: boolean; data: CategoriaOption[] }>(
          `/api/modelli-configurazione/categorie${params}`
        );
        setCategorie(c.data || []);
      } catch { /* silenzioso */ }
    };
    load();
  }, [societaId]);

  const handleSalva = () => {
    if (!tipologia) { showToast('La tipologia è obbligatoria', 'error'); return; }
    onSalva({
      id: componente?.id,
      tipologia,
      categoriaMerceologicaId,
      categoriaNome,
      quantita: quantita !== '' ? Number(quantita) : null,
      isPrincipale,
      isInDescrizione,
    });
  };

  return (
    <div className="bop-modal-overlay">
      <div className="bop-modal" onClick={e => e.stopPropagation()}>
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">{isEdit ? 'Modifica componente' : 'Nuovo componente'}</h2>
          <button className="bop-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="bop-modal__body">

          <div className="bop-form-row">
            <div className="bop-field">
              <label className="bop-field__label">Tipologia <span className="bop-field__required">*</span></label>
              <select
                className="bop-field__select"
                value={tipologia}
                onChange={e => setTipologia(e.target.value)}
              >
                <option value="">Seleziona...</option>
                {tipologie.map(t => (
                  <option key={t.valore} value={t.valore}>{t.label}</option>
                ))}
              </select>
              <p className="bop-field__hint">Caricata da anagrafica — non hardcoded</p>
            </div>
            <div className="bop-field">
              <label className="bop-field__label">Categoria merceologica <span className="bop-field__required">*</span></label>
              <select
                className="bop-field__select"
                value={categoriaMerceologicaId ?? ''}
                onChange={e => {
                  const id = e.target.value ? Number(e.target.value) : null;
                  setCategoriaMerceologicaId(id);
                  const cat = categorie.find(c => c.id === id);
                  setCategoriaNome(cat?.nome || '');
                }}
              >
                <option value="">Seleziona...</option>
                {categorie.map(c => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="bop-form-row" style={{ marginTop: 'var(--bop-spacing-4)' }}>
            <div className="bop-field">
              <label className="bop-field__label">
                Quantità per {umVenditaSimbolo}
                <Tooltip text="Se non specificata ora, dovrà essere inserita in fase di utilizzo del modello" />
              </label>
              <input
                className="bop-field__input"
                type="number"
                step="0.001"
                min="0"
                value={quantita}
                placeholder="Lascia vuoto per definire in fase d'uso"
                onChange={e => setQuantita(e.target.value)}
              />
              <p className="bop-field__hint">
                Es. 0.1 = 1 sacco ogni 10 {umVenditaSimbolo} · Opzionale
              </p>
            </div>
          </div>

          <div className="mocon-checkbox-group">
            <label className="mocon-checkbox-option">
              <input
                type="checkbox"
                className="mocon-checkbox-option__check"
                checked={isPrincipale}
                onChange={e => setIsPrincipale(e.target.checked)}
              />
              <div>
                <span className="mocon-checkbox-option__label">Componente principale</span>
                <p className="mocon-checkbox-option__hint">
                  È l'articolo che il cliente vede come prodotto acquistato (possono esserci più componenti principali)
                </p>
              </div>
            </label>
            <label className="mocon-checkbox-option">
              <input
                type="checkbox"
                className="mocon-checkbox-option__check"
                checked={isInDescrizione}
                onChange={e => setIsInDescrizione(e.target.checked)}
              />
              <div>
                <span className="mocon-checkbox-option__label">Includi nella descrizione documento</span>
                <p className="mocon-checkbox-option__hint">
                  Rende disponibile il placeholder <code>{'{componente_N}'}</code> nel template descrizione
                </p>
              </div>
            </label>
          </div>

        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          <button className="bop-btn bop-btn-primary" onClick={handleSalva}>
            {isEdit ? 'Salva modifiche' : 'Aggiungi componente'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Vista Lista ───────────────────────────────────────────────────────────────

function VistaLista({
  societaId,
  listaSocieta,
  isUnica,
  setSocietaId,
  onEdit,
  onNuovo,
  onDuplica,
}: {
  societaId: string;
  listaSocieta: { id: number; nome: string }[];
  isUnica: boolean;
  setSocietaId: (id: string) => void;
  onEdit: (id: number) => void;
  onNuovo: () => void;
  onDuplica: (draft: DraftModello) => void;
}) {
  const { can } = usePermesso();
  const [modelli, setModelli] = useState<ModelloRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const caricaModelli = useCallback(async () => {
    if (!societaId) { setModelli([]); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ societaId });
      // @api GET /api/modelli-configurazione?societaId&q -> [{id,codice,nome,articoloPadreCodice,articoloPadreNome,umVenditaSimbolo,nComponenti}]
      const res = await apiFetch<{ success: boolean; data: ModelloRow[] }>(
        `/api/modelli-configurazione?${params}`
      );
      setModelli(res.data || []);
    } catch {
      showToast('Errore nel caricamento dei modelli', 'error');
    } finally {
      setLoading(false);
    }
  }, [societaId]);

  useEffect(() => { caricaModelli(); }, [caricaModelli]);

  const filtered = modelli.filter(m =>
    m.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.codice.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.articoloPadreCodice.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.articoloPadreNome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleElimina = async (m: ModelloRow) => {
    if (!window.confirm(`Eliminare il modello "${m.nome}"? I componenti associati saranno eliminati.`)) return;
    try {
      // @api DELETE /api/modelli-configurazione/:id -> {success:true}
      await apiFetch(`/api/modelli-configurazione/${m.id}`, { method: 'DELETE' });
      showToast('Modello eliminato', 'success');
      caricaModelli();
    } catch {
      showToast("Errore nell'eliminazione del modello", 'error');
    }
  };

  const handleDuplica = async (m: ModelloRow) => {
    if (!societaId) return;
    try {
      // Carica il modello sorgente con tutti i componenti, poi apre la pagina
      // di dettaglio in modalità creazione con la bozza pre-popolata.
      // Nessuna POST viene fatta qui: il record verrà persistito a DB solo
      // quando l'utente cliccherà "Salva modello" nella pagina di dettaglio.
      // @api GET /api/modelli-configurazione/:id -> {id,codice,nome,descrizioneTemplate,componenti:[...]}
      const res = await apiFetch<{ success: boolean; data: ModelloDettaglio }>(
        `/api/modelli-configurazione/${m.id}`
      );
      const src = res.data;
      onDuplica({
        nome: `${src.nome} (copia)`,
        articoloPadreId: src.articoloPadreId,
        articoloPadreCodice: src.articoloPadreCodice,
        articoloPadreNome: src.articoloPadreNome,
        umVenditaSimbolo: src.umVenditaSimbolo,
        descrizioneTemplate: src.descrizioneTemplate || '',
        // Componenti senza id DB: saranno creati ex-novo al salvataggio.
        componenti: src.componenti.map(c => ({
          _key: crypto.randomUUID(),
          ordine: c.ordine,
          tipologia: c.tipologia,
          categoriaMerceologicaId: c.categoriaMerceologicaId,
          categoriaNome: c.categoriaNome,
          quantita: c.quantita,
          isPrincipale: c.isPrincipale,
          isInDescrizione: c.isInDescrizione,
        })),
      });
    } catch {
      showToast('Errore nella duplicazione del modello', 'error');
    }
  };

  const noSocieta = !societaId;

  // Unità di misura presenti nei dati → opzioni del filtro enum
  const umOptions = useMemo(() => {
    const um = Array.from(new Set(modelli.map(m => m.umVenditaSimbolo).filter(Boolean))).sort();
    return um.map(u => ({ value: u, label: u }));
  }, [modelli]);

  const columns = useMemo<BopExcelColumn<ModelloRow>[]>(() => [
    {
      key: 'codice',
      header: 'Codice',
      accessor: (m) => m.codice,
      filter: { type: 'text' },
      width: '140px',
      render: (m) => <span className="bop-badge bop-badge--info">{m.codice}</span>,
    },
    {
      key: 'nome',
      header: 'Nome modello',
      accessor: (m) => m.nome,
      filter: { type: 'text' },
      cellClassName: 'bop-modelli__nome',
      render: (m) => m.nome,
    },
    {
      key: 'articoloPadre',
      header: 'Articolo padre',
      accessor: (m) => m.articoloPadreCodice,
      filter: { type: 'text' },
      render: (m) => (
        <>
          <span className="bop-text-muted bop-modelli__padre-codice">{m.articoloPadreCodice}</span>
          <br />
          <span>{m.articoloPadreNome}</span>
        </>
      ),
    },
    {
      key: 'um',
      header: 'UM',
      accessor: (m) => m.umVenditaSimbolo,
      filter: { type: 'enum', options: umOptions },
      width: '90px',
      render: (m) => <span className="bop-badge bop-badge--neutral">{m.umVenditaSimbolo}</span>,
    },
    {
      key: 'nComponenti',
      header: 'Componenti',
      accessor: (m) => m.nComponenti,
      filter: { type: 'number' },
      width: '120px',
      cellClassName: 'bop-text-center',
      headerClassName: 'bop-text-center',
      render: (m) => <span className="bop-badge bop-badge--primary">{m.nComponenti}</span>,
    },
  ], [umOptions]);

  return (
    <Layout>
      <div className="bop-page">

        <div className="bop-page__header">
          <div>
            <h1 className="bop-page__title">Modelli di configurazione</h1>
            <p className="bop-page__subtitle">Definisci i modelli di distinta base per gli articoli configurati</p>
          </div>
          <div className="bop-page__actions">
            {can('archivi.modelli_configurazione', 'edit') && (
              <button
                className="bop-btn bop-btn-primary"
                onClick={onNuovo}
                disabled={!societaId}
              >
                <PlusIcon /> Nuovo modello
              </button>
            )}
          </div>
        </div>

        <div className="bop-filter-bar">
          {!isUnica && (
            <div className="bop-field" style={{ maxWidth: 280 }}>
              <label className="bop-field__label">Società</label>
              <select
                className="bop-field__select"
                value={societaId}
                onChange={e => setSocietaId(e.target.value)}
              >
                <option value="">Tutte le società</option>
                {listaSocieta.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}
          <div className="bop-field" style={{ maxWidth: 320 }}>
            <label className="bop-field__label">Cerca</label>
            <input
              className="bop-field__input"
              placeholder="Codice, nome, articolo padre..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {noSocieta ? (
          <div className="bop-empty-state">
            <span className="bop-empty-state__icon">🏢</span>
            <p className="bop-empty-state__text">Seleziona una società per visualizzare i modelli</p>
          </div>
        ) : (
          <BopExcelTable<ModelloRow>
            data={filtered}
            loading={loading}
            rowKey={(m) => m.id}
            onRowClick={(m) => onEdit(m.id)}
            initialSort={{ key: 'codice', dir: 'asc' }}
            emptyState={
              <div className="bop-empty-state">
                <span className="bop-empty-state__icon">📋</span>
                <p className="bop-empty-state__text">Nessun modello trovato</p>
              </div>
            }
            columns={columns}
            rowActions={(m) => (
              <BopRowActions
                actions={[
                  { icon: <RowIcon.Edit />, label: 'Modifica', onClick: () => onEdit(m.id), variant: 'primary' },
                  { icon: <RowIcon.Copy />, label: 'Duplica', onClick: () => handleDuplica(m), hidden: !can('archivi.modelli_configurazione', 'edit') },
                  { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => handleElimina(m), variant: 'danger', hidden: !can('archivi.modelli_configurazione', 'delete') },
                ]}
              />
            )}
          />
        )}

      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}

// ── Vista Dettaglio ───────────────────────────────────────────────────────────

function VistaDettaglio({
  modelloId,
  initialDraft,
  societaId,
  onBack,
  onCreated,
}: {
  modelloId: number | null;
  initialDraft?: DraftModello;
  societaId: string;
  onBack: () => void;
  onCreated?: (id: number) => void;
}) {
  const { can } = usePermesso();
  const isCreate = modelloId === null;
  const [modello, setModello] = useState<ModelloDettaglio | null>(null);
  const [nome, setNome] = useState('');
  const [articoloPadre, setArticoloPadre] = useState<ArticoloPadreOption | null>(null);
  const [umVenditaSimbolo, setUmVenditaSimbolo] = useState('');
  const [descrizioneTemplate, setDescrizioneTemplate] = useState('');
  const [componenti, setComponenti] = useState<ComponenteLocale[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isCreate);
  const [showModalComponente, setShowModalComponente] = useState(false);
  const [componenteEdit, setComponenteEdit] = useState<ComponenteLocale | null>(null);
  const dragOverKey = useRef<string | null>(null);
  const dragFromKey = useRef<string | null>(null);

  const caricaDettaglio = useCallback(async () => {
    if (isCreate) {
      // Modalità creazione: nessuna chiamata GET, lo stato viene popolato
      // dalla bozza in memoria (vuota per "Nuovo", pre-popolata per "Duplica").
      if (initialDraft) {
        setNome(initialDraft.nome);
        setDescrizioneTemplate(initialDraft.descrizioneTemplate || '');
        setUmVenditaSimbolo(initialDraft.umVenditaSimbolo);
        setArticoloPadre({
          id: initialDraft.articoloPadreId,
          codice: initialDraft.articoloPadreCodice,
          descrizioneBase: initialDraft.articoloPadreNome,
          umVenditaSimbolo: initialDraft.umVenditaSimbolo,
        });
        setComponenti(
          initialDraft.componenti.map(c => ({
            ...c,
            _key: c._key || crypto.randomUUID(),
          }))
        );
      }
      setIsDirty(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // @api GET /api/modelli-configurazione/:id -> {id,codice,nome,descrizioneTemplate,componenti:[...]}
      const res = await apiFetch<{ success: boolean; data: ModelloDettaglio }>(
        `/api/modelli-configurazione/${modelloId}`
      );
      const d = res.data;
      setModello(d);
      setNome(d.nome);
      setDescrizioneTemplate(d.descrizioneTemplate || '');
      setUmVenditaSimbolo(d.umVenditaSimbolo);
      setArticoloPadre({
        id: d.articoloPadreId,
        codice: d.articoloPadreCodice,
        descrizioneBase: d.articoloPadreNome,
        umVenditaSimbolo: d.umVenditaSimbolo,
      });
      setComponenti(d.componenti.map(c => ({ ...c, _key: crypto.randomUUID() })));
      setIsDirty(false);
    } catch {
      showToast('Errore nel caricamento del modello', 'error');
    } finally {
      setLoading(false);
    }
  }, [modelloId, isCreate, initialDraft]);

  useEffect(() => { caricaDettaglio(); }, [caricaDettaglio]);

  const handleBack = () => {
    if (isDirty && !window.confirm('Hai modifiche non salvate. Continuare?')) return;
    onBack();
  };

  const handleSalva = async () => {
    if (!nome.trim()) { showToast('Il nome è obbligatorio', 'error'); return; }
    if (!articoloPadre) { showToast("L'articolo padre è obbligatorio", 'error'); return; }
    setSaving(true);
    try {
      // In modifica l'id serve al backend per aggiornare il componente in loco
      // (le righe di ordini/preventivi lo referenziano); in creazione viene ignorato.
      const componentiPayload = componenti.map(({ _key, ...c }) => (isCreate ? { ...c, id: undefined } : c));

      if (isCreate) {
        // @api POST /api/modelli-configurazione {societaId,nome,articoloPadreId,descrizioneTemplate,componenti:[...]} -> modello completo
        const res = await apiFetch<{ success: boolean; data: ModelloDettaglio }>(
          '/api/modelli-configurazione',
          {
            method: 'POST',
            body: JSON.stringify({
              societaId: Number(societaId),
              nome,
              articoloPadreId: articoloPadre.id,
              descrizioneTemplate,
              componenti: componentiPayload,
            }),
          }
        );
        showToast(`Modello ${res.data.codice} creato`, 'success');
        setIsDirty(false);
        onCreated?.(res.data.id);
        return;
      }

      // @api PUT /api/modelli-configurazione/:id {nome,articoloPadreId,descrizioneTemplate,componenti:[...]} -> modello completo
      const res = await apiFetch<{ success: boolean; data: ModelloDettaglio }>(
        `/api/modelli-configurazione/${modelloId}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            nome,
            articoloPadreId: articoloPadre.id,
            descrizioneTemplate,
            componenti: componentiPayload,
          }),
        }
      );
      const d = res.data;
      setModello(d);
      setNome(d.nome);
      setUmVenditaSimbolo(d.umVenditaSimbolo);
      setArticoloPadre({
        id: d.articoloPadreId,
        codice: d.articoloPadreCodice,
        descrizioneBase: d.articoloPadreNome,
        umVenditaSimbolo: d.umVenditaSimbolo,
      });
      setComponenti(d.componenti.map(c => ({ ...c, _key: crypto.randomUUID() })));
      setIsDirty(false);
      showToast('Modello salvato', 'success');
    } catch (err) {
      showToast(err instanceof Error && err.message ? err.message : 'Errore nel salvataggio del modello', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Aggiorna UM quando cambia l'articolo padre
  const handleSelectArticoloPadre = (a: ArticoloPadreOption) => {
    setArticoloPadre(a);
    setUmVenditaSimbolo(a.umVenditaSimbolo);
    setIsDirty(true);
  };

  const handleClearArticoloPadre = () => {
    setArticoloPadre(null);
    setUmVenditaSimbolo('');
    setIsDirty(true);
  };

  // Componenti
  const openNuovoComponente = (c?: ComponenteLocale) => {
    setComponenteEdit(c || null);
    setShowModalComponente(true);
  };

  const handleSalvaComponente = (dati: Omit<ComponenteLocale, '_key' | 'ordine'>) => {
    setComponenti(prev => {
      if (componenteEdit) {
        return prev.map(c => c._key === componenteEdit._key ? { ...c, ...dati } : c);
      } else {
        const newOrdine = prev.length + 1;
        return [...prev, { ...dati, _key: crypto.randomUUID(), ordine: newOrdine }];
      }
    });
    setShowModalComponente(false);
    setComponenteEdit(null);
    setIsDirty(true);
  };

  const eliminaComponente = (key: string) => {
    setComponenti(prev => {
      const filtered = prev.filter(c => c._key !== key);
      return filtered.map((c, i) => ({ ...c, ordine: i + 1 }));
    });
    setIsDirty(true);
  };

  // Drag & drop
  const handleDragStart = (key: string) => { dragFromKey.current = key; };
  const handleDragOver = (e: React.DragEvent, key: string) => {
    e.preventDefault();
    dragOverKey.current = key;
  };
  const handleDrop = (e: React.DragEvent, targetKey: string) => {
    e.preventDefault();
    const fromKey = dragFromKey.current;
    if (!fromKey || fromKey === targetKey) return;
    setComponenti(prev => {
      const arr = [...prev];
      const fromIdx = arr.findIndex(c => c._key === fromKey);
      const toIdx = arr.findIndex(c => c._key === targetKey);
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr.map((c, i) => ({ ...c, ordine: i + 1 }));
    });
    dragFromKey.current = null;
    dragOverKey.current = null;
    setIsDirty(true);
  };

  // Template placeholder
  const inserisciPlaceholder = (ph: string) => {
    setDescrizioneTemplate(prev => prev + ph);
    setIsDirty(true);
  };

  // Anteprima template
  const anteprimaTemplate = (tpl: string) => {
    return tpl
      .replace(/\{componente_(\d+)\}/g, (_, n) => {
        const comp = componenti.find(c => c.ordine === Number(n));
        return comp ? `[${comp.categoriaNome || comp.tipologia}]` : `{componente_${n}}`;
      });
  };

  if (loading) {
    return (
      <Layout>
        <div className="bop-page">
          <div className="bop-empty-state">
            <span className="bop-empty-state__icon">⏳</span>
            <p className="bop-empty-state__text">Caricamento modello...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isCreate && !modello) {
    return (
      <Layout>
        <div className="bop-page">
          <div className="bop-empty-state">
            <span className="bop-empty-state__icon">❌</span>
            <p className="bop-empty-state__text">Modello non trovato</p>
          </div>
          <button className="bop-btn bop-btn-secondary" onClick={onBack}>← Torna ai modelli</button>
        </div>
      </Layout>
    );
  }

  const componentiInDescrizione = componenti.filter(c => c.isInDescrizione);

  return (
    <Layout>
      <div className="bop-page">

        {/* Header */}
        <div className="bop-page__header">
          <div>
            <button className="bop-btn bop-btn-ghost bop-btn-sm" onClick={handleBack} style={{ marginBottom: 'var(--bop-spacing-2)' }}>
              ← Torna ai modelli
            </button>
            <h1 className="bop-page__title">{nome || (isCreate ? 'Nuovo modello' : '')}</h1>
            <p className="bop-page__subtitle">
              {isCreate ? (
                <span className="bop-badge bop-badge--warning" style={{ marginRight: 'var(--bop-spacing-2)' }}>Bozza non salvata</span>
              ) : (
                <span className="bop-badge bop-badge--info" style={{ marginRight: 'var(--bop-spacing-2)' }}>{modello!.codice}</span>
              )}
              Articolo padre: <strong>{articoloPadre?.codice || '—'}</strong> — {articoloPadre?.descrizioneBase || '—'}
              &nbsp;|&nbsp; UM: <strong>{umVenditaSimbolo || '—'}</strong>
            </p>
          </div>
          <div className="bop-page__actions">
            <button className="bop-btn bop-btn-secondary" onClick={handleBack}>Annulla</button>
            {can('archivi.modelli_configurazione', 'edit') && (
              <button className="bop-btn bop-btn-primary" onClick={handleSalva} disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva modello'}
              </button>
            )}
          </div>
        </div>

        {/* Card Dati modello */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h3 className="bop-card__title">Dati modello</h3>
          </div>
          <div className="bop-card__body">
            <div className="bop-form-row">
              <div className="bop-field">
                <label className="bop-field__label">Codice</label>
                <input
                  className="bop-field__input"
                  value={isCreate ? 'MOCON…' : modello!.codice}
                  disabled
                />
                <p className="bop-field__hint">
                  {isCreate ? 'Generato automaticamente al salvataggio' : 'Generato automaticamente con prefisso MOCON'}
                </p>
              </div>
              <div className="bop-field" style={{ flex: 2 }}>
                <label className="bop-field__label">Nome modello <span className="bop-field__required">*</span></label>
                <input
                  className="bop-field__input"
                  value={nome}
                  onChange={e => { setNome(e.target.value); setIsDirty(true); }}
                />
              </div>
            </div>
            <div className="bop-form-row" style={{ marginTop: 'var(--bop-spacing-4)' }}>
              <div className="bop-field" style={{ flex: 2 }}>
                <label className="bop-field__label">Articolo padre <span className="bop-field__required">*</span></label>
                <AutocompleteArticoloPadre
                  societaId={societaId}
                  selected={articoloPadre}
                  onSelect={handleSelectArticoloPadre}
                  onClear={handleClearArticoloPadre}
                />
                <p className="bop-field__hint">Solo articoli di tipologia PRODOTTO_CONFIGURATO</p>
              </div>
              <div className="bop-field">
                <label className="bop-field__label">UM di vendita</label>
                <input className="bop-field__input" value={umVenditaSimbolo} disabled />
                <p className="bop-field__hint">Ereditata dall'articolo padre</p>
              </div>
            </div>
          </div>
        </div>

        {/* Card Componenti */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h3 className="bop-card__title">
              Componenti
              <span className="bop-badge bop-badge--primary" style={{ marginLeft: 'var(--bop-spacing-2)' }}>{componenti.length}</span>
            </h3>
            {can('archivi.modelli_configurazione', 'edit') && (
              <button className="bop-btn bop-btn-primary bop-btn-sm" onClick={() => openNuovoComponente()}>
                <PlusIcon /> Aggiungi componente
              </button>
            )}
          </div>
          <div className="bop-card__body" style={{ padding: 0 }}>
            <table className="bop-table">
              <thead>
                <tr>
                  <th className="bop-table__th" style={{ width: 40 }}></th>
                  <th className="bop-table__th" style={{ width: 40 }}>#</th>
                  <th className="bop-table__th">Tipologia</th>
                  <th className="bop-table__th">Categoria</th>
                  <th className="bop-table__th" style={{ width: 140 }}>
                    Qtà / UM
                    <Tooltip text="Se non specificata, sarà da inserire in fase di utilizzo del modello" />
                  </th>
                  <th className="bop-table__th bop-text-center" style={{ width: 100 }}>Principale</th>
                  <th className="bop-table__th bop-text-center" style={{ width: 110 }}>In descriz.</th>
                  <th className="bop-table__th bop-table__th--actions">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {componenti.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="bop-table__empty">
                      <div className="bop-empty-state">
                        <span className="bop-empty-state__icon">🧩</span>
                        <p className="bop-empty-state__text">Nessun componente — aggiungi il primo</p>
                      </div>
                    </td>
                  </tr>
                ) : componenti.map(c => (
                  <tr
                    key={c._key}
                    className="bop-table__row"
                    draggable
                    onDragStart={() => handleDragStart(c._key)}
                    onDragOver={e => handleDragOver(e, c._key)}
                    onDrop={e => handleDrop(e, c._key)}
                  >
                    <td className="bop-table__td">
                      <span className="mocon-drag-handle"><GripIcon /></span>
                    </td>
                    <td className="bop-table__td">
                      <span className="mocon-ordine">{c.ordine}</span>
                    </td>
                    <td className="bop-table__td">
                      <span className={`bop-badge ${c.tipologia === 'LAVORAZIONE_PRESTAZIONE' ? 'bop-badge--warning' : 'bop-badge--info'}`}>
                        {c.tipologia.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="bop-table__td bop-text-bold">{c.categoriaNome}</td>
                    <td className="bop-table__td">
                      {c.quantita !== null
                        ? <span className="mocon-qta-cell">{c.quantita}<span className="mocon-qta-cell__um">{umVenditaSimbolo}</span></span>
                        : <span className="bop-badge bop-badge--warning">da definire</span>
                      }
                    </td>
                    <td className="bop-table__td bop-text-center">
                      {c.isPrincipale
                        ? <span className="bop-badge bop-badge--success">✓ Sì</span>
                        : <span className="bop-badge bop-badge--neutral">No</span>}
                    </td>
                    <td className="bop-table__td bop-text-center">
                      {c.isInDescrizione
                        ? <span className="mocon-ph-badge">{`{comp_${c.ordine}}`}</span>
                        : <span className="bop-text-muted">—</span>}
                    </td>
                    <td className="bop-table__td bop-table__td--actions">
                      {can('archivi.modelli_configurazione', 'edit') && (
                        <>
                          <button
                            className="bop-btn bop-btn-ghost bop-btn-sm"
                            onClick={() => openNuovoComponente(c)}
                          >
                            <EditIcon />
                          </button>
                          <button
                            className="bop-btn bop-btn-ghost bop-btn-sm bop-text-danger"
                            onClick={() => eliminaComponente(c._key)}
                            disabled={componenti.length <= 1}
                          >
                            <TrashIcon />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Card Descrizione documento */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h3 className="bop-card__title">Descrizione documento</h3>
          </div>
          <div className="bop-card__body">
            <p className="bop-text-muted" style={{ fontSize: 'var(--bop-font-sm)', marginBottom: 'var(--bop-spacing-3)' }}>
              Componi il testo che apparirà sui documenti. Usa i placeholder dei componenti per inserire le descrizioni degli articoli scelti al momento della configurazione.
            </p>

            <div className="mocon-chips">
              {componentiInDescrizione.map(c => (
                <button
                  key={c._key}
                  className="bop-btn bop-btn-secondary bop-btn-sm"
                  onClick={() => inserisciPlaceholder(`{componente_${c.ordine}}`)}
                  title={`Inserisce la descrizione di: ${c.categoriaNome}`}
                >
                  + {`{componente_${c.ordine}}`}
                  <span className="bop-text-muted" style={{ fontSize: 'var(--bop-font-xs)', marginLeft: 4 }}>({c.categoriaNome})</span>
                </button>
              ))}
              {componentiInDescrizione.length === 0 && (
                <span className="bop-text-muted" style={{ fontSize: 'var(--bop-font-sm)' }}>
                  Nessun componente con "Includi nella descrizione" attivo
                </span>
              )}
            </div>

            <div className="bop-field">
              <label className="bop-field__label">Template descrizione</label>
              <textarea
                className="bop-field__input"
                rows={4}
                value={descrizioneTemplate}
                onChange={e => { setDescrizioneTemplate(e.target.value); setIsDirty(true); }}
                style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 'var(--bop-font-sm)' }}
              />
              <p className="bop-field__hint">
                I placeholder <code>{'{componente_N}'}</code> saranno sostituiti con la descrizione breve dell'articolo scelto nel wizard
              </p>
            </div>

            <div className="mocon-template-preview">
              <span className="mocon-template-preview__label">Anteprima (dati fittizi)</span>
              <span className="mocon-template-preview__text">
                {descrizioneTemplate
                  ? anteprimaTemplate(descrizioneTemplate)
                  : <em className="bop-text-muted">Nessun template definito</em>
                }
              </span>
            </div>
          </div>
        </div>

      </div>

      {showModalComponente && (
        <ModalComponente
          componente={componenteEdit}
          umVenditaSimbolo={umVenditaSimbolo}
          societaId={societaId}
          onClose={() => { setShowModalComponente(false); setComponenteEdit(null); }}
          onSalva={handleSalvaComponente}
        />
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}

// ── Componente principale ─────────────────────────────────────────────────────

export default function ModelliConfigurazionePage() {
  const { societaId, setSocietaId, listaSocieta, loading, isUnica } = useFiltroSocieta();
  const [view, setView] = useState<'list' | 'detail' | 'create'>('list');
  const [selectedModelloId, setSelectedModelloId] = useState<number | null>(null);
  const [draftModello, setDraftModello] = useState<DraftModello | null>(null);
  const [showModalNuovo, setShowModalNuovo] = useState(false);

  // Reset alla lista quando cambia la società
  useEffect(() => {
    setView('list');
    setSelectedModelloId(null);
    setDraftModello(null);
  }, [societaId]);

  const openDetail = (id: number) => {
    setDraftModello(null);
    setSelectedModelloId(id);
    setView('detail');
  };

  const openCreate = (draft: DraftModello) => {
    setSelectedModelloId(null);
    setDraftModello(draft);
    setView('create');
  };

  const backToList = () => {
    setView('list');
    setSelectedModelloId(null);
    setDraftModello(null);
  };

  // Quando l'utente conferma "Prosegui" sulla modale → apre la pagina di
  // dettaglio in modalità creazione con una bozza vuota.
  const handleNuovoConfigured = (draft: DraftModello) => {
    setShowModalNuovo(false);
    openCreate(draft);
  };

  // Dopo POST riuscita in modalità creazione → switcha a vista dettaglio
  // dell'id appena creato.
  const handleCreated = (id: number) => {
    openDetail(id);
  };

  if (loading) {
    return (
      <Layout>
        <div className="bop-page">
          <div className="bop-empty-state">
            <p className="bop-empty-state__text">Caricamento...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (view === 'detail' && selectedModelloId !== null) {
    return (
      <VistaDettaglio
        modelloId={selectedModelloId}
        societaId={societaId}
        onBack={backToList}
      />
    );
  }

  if (view === 'create' && draftModello) {
    return (
      <VistaDettaglio
        modelloId={null}
        initialDraft={draftModello}
        societaId={societaId}
        onBack={backToList}
        onCreated={handleCreated}
      />
    );
  }

  return (
    <>
      <VistaLista
        societaId={societaId}
        listaSocieta={listaSocieta}
        isUnica={isUnica}
        setSocietaId={setSocietaId}
        onEdit={openDetail}
        onNuovo={() => setShowModalNuovo(true)}
        onDuplica={openCreate}
      />

      {showModalNuovo && (
        <ModalNuovoModello
          societaId={societaId}
          onClose={() => setShowModalNuovo(false)}
          onConfigured={handleNuovoConfigured}
        />
      )}
    </>
  );
}
